import { DateTime } from 'luxon';
import numeral from 'numeral';
import { List as ImList, Set as ImmutableSet, Map as ImMap, RecordOf } from 'immutable';
import store from 'store';

import type RootState from 'companion-app-components/utils/redux-store/rootState';
import type { Account, AccountProps } from 'companion-app-components/flux/accounts/accountsTypes';
import type { ChartOfAccount, ChartOfAccountProps } from 'companion-app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { Tag } from 'companion-app-components/flux/tags/tagsTypes';
import type {
  SplitItem,
  SplitProps,
  SplitRecord,
  TransactionProps,
  TransactionRecord,
} from 'companion-app-components/flux/transactions/transactionsTypes';
import type { ScheduledTransaction } from 'companion-app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { accountsUtils, accountsSelectors } from 'companion-app-components/flux/accounts';
import { tagsSelectors } from 'companion-app-components/flux/tags';
import { transactionsTypes, transactionsUtils, transactionsTransformers } from 'companion-app-components/flux/transactions';
import { chartOfAccountsTypes, chartOfAccountsUtils } from 'companion-app-components/flux/chart-of-accounts';
import { categoriesSelectors } from 'companion-app-components/flux/categories';
import { authSelectors } from 'companion-app-components/flux/auth';
import { isAccountDisconnected } from 'companion-app-components/flux/accounts/accountsUtils';

import { memoizeWithKey } from 'utils/memoizeWithKey';
import { noNaN } from 'utils/utils';
import { getCurrencyString } from 'data/accounts/retrievers';
import { sumSplitRows } from 'data/transactions/splitsHelpers';
import { TransactionStatus, TxnStateInfoType } from './types';

const DATE_FORMAT = 'MM/dd/yyyy';

//-----------------------------------------------------------------------
// given an immutable list of transactions, will return the currency symbol appropriate to represent
// the group.  Either the one shared currency, or "N/A" if they differ
//
// pass in an array of transaction arrays (support for multiple transaction groups), if you only have
// one transaction list, pass in [txList]
export function getSharedCurrency(txns: ImList<TransactionRecord>[], accounts: ImMap<string, RecordOf<AccountProps>>) {
  let currency: null | undefined | string = null;
  if (txns && accounts && accounts.size > 0) {
    txns.forEach((txnList) => {
      txnList.forEach((txn) => {
        const acctRec = accounts.get(String(txn.accountId));
        if (acctRec) {
          const tc = acctRec.get('currency');
          if (tc !== currency) {
            currency = !currency ? tc : 'N/A';
          }
        }
      });
    });
  }
  return currency;
}

//-----------------------------------------------------------------------
// getMatchState
//
// For any transaction will return the following
//
// 'manual' - the transaction has no cpData
// 'matched' - the transaction has cpData and is matchstate is one of the MATCHED states
// 'downloaded' - the transaction has cpData, is not matched, and is not reviewed
export function getMatchState(txn: TransactionRecord) {
  if (txn.cpData) {
    if (txn.matchState === 'AUTO_MATCHED' || txn.matchState === 'USER_MATCHED') {
      return 'matched';
    }
    return 'downloaded';
  }
  return 'manual';
}

/*
 * getTxnStateInfo
 *
 * This is a mapping from txn SOURCE and STATUS to an object that indicates display information for the transaction
 * object returns contains 'entry', which is either 'bank', 'matched', 'manual', or 'scheduled', and a status which is a combination of
 * transaction state and source, and is one of the following:
 * PENDING
 * CLEARED
 * UPCOMING
 * DUE (Due Today)
 * OVERDUE
 */
export function getTxnStateInfo(txn: TransactionRecord) {
  const today: DateTime = DateTime.local().startOf('day');
  const txnDay: DateTime = DateTime.fromISO(txn.postedOn).startOf('day');
  const future: boolean = txnDay > today;

  const retObj: TxnStateInfoType = {
    entry: getMatchState(txn) === 'matched' ? 'matched' : 'bank',
    status: txn.state,
  };

  switch (txn.source && txn.source.trim()) {
    case 'QCS_REFRESH':
    case 'QCS_BATCH_REFRESH':
    case 'CLIENT_AGGREGATED':
      break;
    case 'QCS_REFRESH_BANK_PENDING':
    case 'QCS_BATCH_REFRESH_BANK_PENDING':
    case 'REAL_TIME':
      break;
    case 'USER_ENTERED':
      retObj.entry = getMatchState(txn) === 'matched' ? 'matched' : 'manual';
      if (txn.state === 'CLEARED' || txn.state === 'RECONCILED') {
        retObj.status = txn.state;
      } else {
        retObj.status = future ? 'UPCOMING' : 'PENDING';
      }
      break;
    case 'USER_OWNED_BANK_PENDING':
    case 'USER_OWNED_REAL_TIME_BANK_PENDING':
      break;
    case 'SCHEDULED_TRANSACTION_PENDING':
      retObj.status = future ? 'UPCOMING' : 'OVERDUE';
      if (txnDay.hasSame(today, 'day')) {
        retObj.status = 'DUE';
      }
      retObj.entry = retObj.entry === 'matched' ? 'matched' : 'scheduled';
      break;
    case 'SCHEDULED_TRANSACTION_UNKNOWN':
    case 'SCHEDULED_TRANSACTION': {
      if (txn.state === 'PENDING') {
        retObj.status = future ? 'UPCOMING' : 'PENDING';
      } else {
        retObj.status = 'CLEARED';
      }
      retObj.entry = retObj.entry === 'matched' ? 'matched' : 'scheduled';
      break;
    }
    case 'SCHEDULED_TRANSACTION_SKIPPED':
      // not applicable, this transaction should not be shown (soft deleted)
      break;
    case 'UNKNOWN':
      // should never happen - throw an event?
      retObj.entry = 'manual';
      retObj.status = future ? 'UPCOMING' : 'CLEARED';
      break;
    default:
      // this should REALLY never happen
      break;
  }

  return retObj;
}

export function isExistingTxn(txn: TransactionRecord) {
  return txn?.id && txn.id !== '0';
}

function currenciesAreValidAndDifferent(strCurrency1: string, strCurrency2: string) {
  if (strCurrency1 === 'N/A' || strCurrency2 === 'N/A') {
    return false;
  }
  return strCurrency1 !== strCurrency2;
}

// -------------------------------------------------------
// setReportsFlags
export function setReportsFlags(txn: TransactionRecord, value: any) {
  return txn && txn.set('isExcludedFromReports', Boolean(value)).set('isExcludedFromF2S', Boolean(value));
}

// -------------------------------------------------------
// isMultiCurrencyTransferTxn
export function isMultiCurrencyTransferTxn(txn: TransactionRecord, state: RootState) {
  let isMultiCurrency = false;
  const txnCurrency = getCurrencyString(txn.accountId, state);
  if (transactionsUtils.isTransferTxn(txn)) {
    if (transactionsUtils.isSplitTxn(txn) && txn.split && txn.split.items) {
      txn.split.items.forEach((item) => {
        if (item.coa.type === 'ACCOUNT') {
          isMultiCurrency = isMultiCurrency || currenciesAreValidAndDifferent(getCurrencyString(item.coa.id, state)!, txnCurrency!);
        }
      });
    } else {
      isMultiCurrency = currenciesAreValidAndDifferent(getCurrencyString(txn?.coa?.id, state)!, txnCurrency!);
    }
  }
  return isMultiCurrency;
}

const pendingStates = [
  'QCS_REFRESH_BANK_PENDING',
  'QCS_BATCH_REFRESH_BANK_PENDING',
  'USER_OWNED_BANK_PENDING',
  'REAL_TIME',
  'USER_OWNED_REAL_TIME_BANK_PENDING',
];

export const isAcceptedScheduledTxn = (txn: TransactionRecord) => txn.source && txn.source === 'SCHEDULED_TRANSACTION';

export const isUnacceptedScheduledTxn = (txn: TransactionRecord) => txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING';

export function isBankPendingTxn(txn: TransactionRecord) {
  return pendingStates.indexOf(txn.source!) >= 0;
}

export function isBankDownloadedTxn(txn: TransactionRecord) {
  return txn && txn.cpData;
}

export function isPendingTxn(txn: TransactionRecord) {
  return txn && txn.state === 'PENDING';
}

export function isNextScheduledInstance(txn: TransactionRecord, scheduledTransactionsById: ImMap<string, ScheduledTransaction>) {
  if (isUnacceptedScheduledTxn(txn)) {
    const model = scheduledTransactionsById.get(txn.stModelId!);
    return model && DateTime.fromISO(model.dueOn!).toFormat(DATE_FORMAT) === DateTime.fromISO(txn.stDueOn!).toFormat(DATE_FORMAT);
  }
  return false;
}

export function isActionableScheduledInstance(
  txn: TransactionRecord,
  scheduledTransactionsById: ImMap<string, ScheduledTransaction>,
) {
  const stateInfo = getTxnStateInfo(txn);
  const { status } = stateInfo;
  return status === 'DUE' || status === 'OVERDUE' || isNextScheduledInstance(txn, scheduledTransactionsById);
}

function sortedScheduledInstancesForAccount(accountTransactions: ImList<TransactionRecord>, modelId: string) {
  let scheduledInstances = accountTransactions.filter(
    (txn: TransactionRecord) => isUnacceptedScheduledTxn(txn) && modelId === txn.stModelId,
  );
  if (scheduledInstances) {
    scheduledInstances = scheduledInstances.toList();
    scheduledInstances = scheduledInstances.sort(
      (txn1, txn2) =>
        // @ts-expect-error - FIXME: type issue ts(2363)
        DateTime.fromISO(txn1.postedOn) - DateTime.fromISO(txn2.postedOn),
    );
  }
  return scheduledInstances;
}

function internalNextScheduledInstanceAfterDate(date: string, accountTransactions: ImList<TransactionRecord>, modelId: string) {
  const scheduledInstances = sortedScheduledInstancesForAccount(accountTransactions, modelId);
  const index = scheduledInstances.findIndex((x) => DateTime.fromISO(x.postedOn) > DateTime.fromISO(date));
  if (index !== -1) {
    return scheduledInstances.get(index);
  }

  return null;
}

export function nextScheduledInstanceAfterDate(date: string, accountTransactions: ImList<TransactionRecord>, modelId: string) {
  const key = `${DateTime.fromISO(date).toFormat('yyyy-MM-dd')}-${modelId}-${accountTransactions.hashCode()}`;
  return memoizeWithKey('nextScheduled', () => internalNextScheduledInstanceAfterDate(date, accountTransactions, modelId), key, 50);

  /* TODO: Keeping this commented piece of code after TS migration
  const index = memoCache.findIndex((x) => x.key === key);
  if (index !== -1) return memoCache[index].value;

  const ret = internalNextScheduledInstanceAfterDate(date, accountTransactions, modelId);
  memoCache.push({key, value: ret});
  if (memoCache.size > 50) {
    memoCache = memoCache.slice(1);
  }
  return ret;
   */
}

export function nextScheduledInstanceAfterTransaction(txn: TransactionRecord, accountTransactions: ImList<TransactionRecord>) {
  const scheduledInstances = sortedScheduledInstancesForAccount(accountTransactions, txn.stModelId!);
  const index = scheduledInstances.findIndex((x) => x.id === txn.id);
  if (index !== -1 && index + 1 < scheduledInstances.size) {
    return scheduledInstances.get(index + 1);
  }
  return null;
}

export const isGoalTxnInNonGoalAccount = (txn: TransactionRecord, goalAccountIds: ImList<string>) =>
  txn.coa && txn.coa.type === 'ACCOUNT' && goalAccountIds.contains(txn.coa.id) && !goalAccountIds.contains(txn.accountId!);

// # ================================================================ #
// # - - -            Split logic/functions                     - - - #
// # ================================================================ #

export function sumSplitItems(split?: SplitRecord | SplitProps | null) {
  let sum = 0;
  if (split && split.items) {
    split.items.forEach((item: SplitItem) => {
      sum += Number(item.amount);
    });
  }
  return sum;
}

// ----------------
// adjustSplitLine
// ----------------
// given a txn and the index of the splitItem just edited, perform the balancing
// and return the updated split
//
// expects an immutable split
export function adjustSplitLine(txn: TransactionRecord, index: number) {
  assert(index !== undefined, 'FORGOT TO PASS IN AN INDEX');
  let newSplit = txn.split as Record<string, any>;
  const { items } = newSplit;
  const diff = Number((Number(txn.amount) - sumSplitItems(newSplit)).toFixed(2));

  function adjustAmountAtIndex(adjustIndex: number) {
    const otherItem = items.get(adjustIndex);
    const newAmount = Number(Number(otherItem.amount + diff).toFixed(2));
    newSplit = newSplit.setIn(['items', adjustIndex, 'amount'], newAmount);
  }

  if (items.size === 2) {
    // 2 items
    const otherIndex = index === 0 ? 1 : 0;
    adjustAmountAtIndex(otherIndex);
  } else {
    // multiple items
    // look for uncategorized item to change (that isn't the item you just edited)
    const uncatIndex = items.findIndex((item, currentIndex) => currentIndex !== index && item.coa.type === 'UNCATEGORIZED');
    if (uncatIndex >= 0) {
      adjustAmountAtIndex(uncatIndex);
    } else {
      // add uncat item with the difference
      newSplit = newSplit.set(
        'items',
        items.push(
          transactionsTypes.mkSplitItem({
            amount: diff,
            coa: { type: 'UNCATEGORIZED', id: '0' },
          }),
        ),
      );
    }
  }

  return newSplit;
}

// ----------------
// removeSplitLine
// ----------------
// given a txn and the index of the splitItem to remove, perform the balancing
// and return the updated split (and category)
//
// expects an immutable split
export function removeSplitLine(txn: TransactionRecord, index: number) {
  assert(index !== undefined, 'FORGOT TO PASS IN AN INDEX');
  let newSplit = txn.split as Record<string, any>;
  const { items } = newSplit;
  const itemToRemove = items.get(index);
  // exit early if deleting a zero amount
  if (itemToRemove.amount === 0) {
    return newSplit.deleteIn(['items', index]);
  }

  function adjustAmountAtIndex(adjustIndex: number) {
    const otherItem = items.get(adjustIndex);
    const newAmount = Number(Number(otherItem.amount + itemToRemove.amount).toFixed(2));
    newSplit = newSplit.setIn(['items', adjustIndex, 'amount'], newAmount);
  }

  // push the amount somewhere else
  // look for uncategorized item to change (that isn't the item to delete)
  const uncatIndex = items.findIndex((item, currentIndex) => currentIndex !== index && item.coa.type === 'UNCATEGORIZED');
  if (uncatIndex !== -1) {
    // uncat found
    adjustAmountAtIndex(uncatIndex);
  } else {
    // look for same category item (that isn't the item to delete)
    const sameCatIndex = items.findIndex(
      (item, currentIndex) =>
        currentIndex !== index && item.coa.type === itemToRemove.coa.type && item.coa.id === itemToRemove.coa.id,
    );
    if (sameCatIndex !== -1) {
      // same cat found
      adjustAmountAtIndex(sameCatIndex);
    } else {
      // push to the top
      const defaultIndex = index === 0 ? 1 : 0;
      adjustAmountAtIndex(defaultIndex);
    }
  }
  newSplit = newSplit.deleteIn(['items', index]);

  return newSplit;
}

// made to work with JS objects and immutables
export function isUnbalancedSplitTxn(txn: TransactionRecord) {
  if (transactionsUtils.isSplitTxn(txn)) {
    return Number(txn.amount).toFixed(2) !== sumSplitItems(txn.split).toFixed(2);
  }

  return false;
}

// made to work with JS objects and immutables
export function balanceSplitTxn(txn) {
  let newTxn = txn;
  if (isUnbalancedSplitTxn(txn)) {
    const isImmutable = txn.split.items.size !== undefined;
    // set toFixed and then back to number to avoid fixed precision calculation error
    const diff = Number((Number(txn.amount) - sumSplitItems(txn.split)).toFixed(2));
    const uncatItem = txn.split.items.findIndex((item) => item.coa.type === 'UNCATEGORIZED');
    // if there is an uncategorized row already, add the difference to it
    if (uncatItem >= 0) {
      const newAmount = Number(
        (Number(isImmutable ? txn.split.items.get(uncatItem).amount : txn.split.items[uncatItem].amount) + diff).toFixed(2),
      );
      if (isImmutable) {
        if (txn.setIn) {
          // txn object is immutable
          newTxn = txn.setIn(['split', 'items', uncatItem, 'amount'], newAmount);
        } else {
          // txn object javascript but split immutable
          newTxn.split = txn.split.setIn(['items', uncatItem, 'amount'], newAmount);
        }
      } else {
        newTxn.split.items[uncatItem].amount = newAmount;
      }

      // otherwise create a new uncategorized row
    } else if (isImmutable) {
      const items = txn.split.items.push(
        transactionsTypes.mkSplitItem({
          amount: diff,
          coa: { type: 'UNCATEGORIZED', id: '0' },
        }),
      );
      if (txn.setIn) {
        // txn object is immutable
        newTxn = txn.setIn(['split', 'items'], items);
      } else {
        // txn object javascript but split immutable
        newTxn.split = txn.split.set('items', items);
      }
    } else {
      newTxn.split.items.push({ coa: { type: 'UNCATEGORIZED', id: '0' }, amount: diff });
    }
  }
  return newTxn;
}

export function isUncategorizedTxn(txn: TransactionRecord) {
  return !transactionsUtils.isSplitTxn(txn) && (!txn.coa || txn.coa.type === 'UNCATEGORIZED');
}

export function txnHasTags(txn) {
  return Boolean(txn.tags && txn.tags.size > 0);
}

export function isSupportedTransferTxn(txn: TransactionRecord, state?: RootState) {
  if (transactionsUtils.isSplitTxn(txn) && txn?.split?.items) {
    return Boolean(txn.split.items.find((item: SplitItem) => chartOfAccountsUtils.isSupportedTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isSupportedTransferCoa(txn.coa, state);
}

export function txnHasUnsupportedTransfer(
  txn: TransactionRecord,
  featureFlags: ImMap<string, any>,
  state: RootState,
) {
  if (!transactionsUtils.isTransferTxn(txn)) return false;
  const hideLoanTransactions = featureFlags.get('hideLoanTransactions');
  const hideConnectedLoanTransactions = featureFlags.get('hideConnectedLoanTransactions');
  if (transactionsUtils.isSplitTxn(txn) && txn?.split?.items) {
    return Boolean(
      // @ts-expect-error - FIXME: type issue with find(). No overload matches this call
      txn.split.items.find(
        (item) =>
          item.coa.type === 'ACCOUNT' &&
          accountsUtils.transactionsNotSupportedForAccountId(
            item.coa.id,
            hideLoanTransactions,
            hideConnectedLoanTransactions,
            state,
          ),
      ),
    );
  }
  return (
    txn?.coa &&
    accountsUtils.transactionsNotSupportedForAccountId(txn.coa.id, hideLoanTransactions, hideConnectedLoanTransactions, state)
  );
}

export function isSupportedTransferTxnWithFlags(txn: TransactionRecord, featureFlags: ImMap<string, any>, state: RootState) {
  if (!transactionsUtils.isTransferTxn(txn)) return false;
  return !txnHasUnsupportedTransfer(txn, featureFlags, state);
}

export function isUnsyncedTransferTxn(txn: TransactionRecord, state: RootState) {
  if (transactionsUtils.isSplitTxn(txn) && txn?.split?.items) {
    return Boolean(txn.split.items.find((item: SplitItem) => chartOfAccountsUtils.isUnsyncedTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isUnsyncedTransferCoa(txn.coa, state);
}

export function isLoanAcctTransferTxn(txn: TransactionRecord, state: RootState) {
  if (transactionsUtils.isSplitTxn(txn) && txn?.split?.items) {
    return Boolean(txn.split.items.find((item: SplitItem) => chartOfAccountsUtils.isLoanAcctTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isLoanAcctTransferCoa(txn.coa, state);
}

export function isInvestmentAcctTransferTxn(txn: TransactionRecord, state: RootState) {
  if (transactionsUtils.isSplitTxn(txn) && txn?.split?.items) {
    return Boolean(txn.split.items.find((item: SplitItem) => chartOfAccountsUtils.isInvestmentAcctTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isInvestmentAcctTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// Returns null if edits are allowed, otherwise returns
// a reason code
// UNSUPPORTED_SCHED_TRANSFER
// BILLPAY_TRANSACTION
//
export function transactionEditsNotAllowed(txn: TransactionRecord, state: RootState) {
  // if this is a transfer, and a scheduled instance make sure it is a legit transfer
  if (transactionsUtils.isTransferTxn(txn) && isUnacceptedScheduledTxn(txn) && !isSupportedTransferTxn(txn, state)) {
    return 'UNSUPPORTED_SCHED_TRANSFER';
  }
  if (txn.paymentProvider === 'OFX') {
    return 'BILLPAY_TRANSACTION';
  }
  return null;
}

export function isBankPending(txn: TransactionRecord) {
  return isBankPendingTxn(txn); // /.+_BANK_PENDING$/.test(txn.source);
}

export function isUserOwned(txn: TransactionRecord) {
  // return (txn.source.indexOf('USER_') === 0 || txn.source.indexOf('SCHEDULED_TRANSACTION') === 0);
  return /^USER_.+/.test(txn.source as string) || /^SCHEDULED_TRANSACTIO.+/.test(txn.source as string);
}

export function isBankOwnedPending(txn: TransactionRecord) {
  return txn.source && isBankPendingTxn(txn) && !isUserOwned(txn);
}

// check is SCHEDULED and BANK_PENDING txn
export function isUnAcceptedTxnType(txn: TransactionRecord) {
  return isUnacceptedScheduledTxn(txn) || isBankOwnedPending(txn);
}

export function isUnAcceptedBankPendingTxn(account: Account, txn: TransactionRecord) {
  return account.isBankPendingTxnsExcluded && isBankOwnedPending(txn);
}

// A "match" is one which has a user-owned txn matched to a posted-txn. This is specifically excluding a
// transactions that are user-owned and matched to bank-pending txn.
export function isMatchedToPostedTxn(txn: TransactionRecord) {
  return isUserOwned(txn) && txn.cpData && txn.cpData.id && txn.matchedTxn?.id;
}

export function isAssociatedWithBankPendingTxn(txn: TransactionRecord) {
  return txn.cpData && (txn.cpData.pendingId || txn.cpData.cpPendingId); // cpPendingId will become pendingId, backward compatible
}

export function areMatchable(txn1: TransactionRecord, txn2: TransactionRecord) {
  let srcTxn: TransactionRecord;
  let dstTxn: TransactionRecord;
  if (isUserOwned(txn1)) {
    if (isUserOwned(txn2)) {
      return false;  // one must be non user-owned
    }
    srcTxn = txn2;
    dstTxn = txn1;
  } else {
    if (!isUserOwned(txn2)) {
      return false; // one must be user-owned
    }
    srcTxn = txn1;
    dstTxn = txn2;
  }
  if (srcTxn.accountId !== dstTxn.accountId) {
    return false; // they must belong to the same account
  }
  if (isMatchedToPostedTxn(dstTxn)) {
    return false; // already matched to posted-txn (need to first unmatch before re-matching)
  }
  if (isBankPending(srcTxn) && isAssociatedWithBankPendingTxn(dstTxn)) {
    return false; // destination txn is already associated with a bank-pending txn
  }
  return true;
}

// used to update txns in preprocess (allTxnsToUpdate/allTxnsToCreate)
export function replaceInArray(txnsArray: TransactionRecord[], txnToReplace: TransactionRecord) {
  const foundIndex = txnsArray.findIndex(
    (txn: TransactionRecord) => txn?.id === txnToReplace?.id || txn?.clientId === txnToReplace?.clientId,
  );
  const found = foundIndex > -1;
  if (found) {
    txnsArray.splice(foundIndex, 1, txnToReplace);
  }
  return found;
}

// Given a user provided amount, proposedSign is either 1 or -1
export function quickenAmountToString(amountStr: string, proposedSign = 1) {
  // this just safety checks the value to ensure it is either 1 or -1
  let sign = (proposedSign / proposedSign || 1) * Math.sign(proposedSign) || 1;
  if (String(amountStr).charAt(0) === '+') {
    sign = 1;
  }
  if (String(amountStr).charAt(0) === '-') {
    sign = -1;
  }

  return numeral(Math.abs(Number(amountStr)) * sign).format('0.00');
}

//-------------------------------------------------------
// findTransaction
//
export function findTransaction(
  txnsByAccountId: ImMap<string, ImMap<string, TransactionRecord>> | undefined,
  txnId: keyof TransactionProps,
  accountId?: string,
) {
  if (accountId) {
    const acctTxns = txnsByAccountId?.get(accountId);
    if (acctTxns) {
      const foundTxn = acctTxns.get(txnId);
      if (foundTxn) return foundTxn;
    }
  }
  // slow way
  let ret: TransactionRecord | null = null;
  txnsByAccountId?.some((txnList) => {
    const foundTxn = txnList.get(txnId);
    if (foundTxn) {
      ret = foundTxn;
      return true;
    }
    return false;
  });

  return ret;
}

export function coasAreDifferent(coa1?: ChartOfAccountProps, coa2?: ChartOfAccountProps) {
  if (!coa1 && !coa2) {
    return false;
  }
  if (!coa1 || !coa2) {
    return true;
  }
  return coa1.type !== coa2.type || coa1.id !== coa2.id;
}

const matchTagIds = (tag1: Tag, tag2: Tag) => (Number(tag1.id) > Number(tag2.id) ? 1 : -1);

const matchSplitCoaIds = (split1: SplitItem, split2: SplitItem) => (Number(split1?.coa?.id) > Number(split2?.coa?.id) ? 1 : -1);

const splitStringVal = (item) => `${item.coa.id}-${Number(item.amount).toFixed(2)}-${item.memo}-${item.tags ? item.tags.length : 'none'}`;

const reducerSplitSum = (total: number, item: SplitItem) => `${total}-${splitStringVal(item)}`;

export function areTagsEqual(tags1: ImList<Tag>, tags2: ImList<Tag>) {
  if (!tags1 && !tags2) {
    return true;
  }
  if (!tags1 || !tags2) {
    return false;
  }
  const sum1 = tags1.sort(matchTagIds).reduce((total, num) => `${total}-${num.id}`, '');
  const sum2 = tags2.sort(matchTagIds).reduce((total, num) => `${total}-${num.id}`, '');
  return sum1 === sum2;
}

export function fieldChanged(txn: TransactionRecord, txnPrev: TransactionRecord, field: string) {
  // this is checking if one or the other is null, however, we have to do a bit more
  // for splits since even though .split may not be null, it can still be a non-split if
  // there are no items
  // should not be called with either value being null
  if (!txn || !txnPrev) {
    return false;
  }

  if (!txn[field] || !txnPrev[field]) {
    return field === 'split'
      ? transactionsUtils.isSplitTxn(txn) !== transactionsUtils.isSplitTxn(txnPrev)
      : Boolean(txn[field]) !== Boolean(txnPrev[field]);
  }
  switch (field) {
    case 'amount': {
      return Number(txnPrev.amount) !== Number(txn.amount);
    }
    case 'postedOn': {
      return txnPrev.postedOn !== txn.postedOn;
    }
    case 'coa': {
      return coasAreDifferent(txnPrev.coa!, txn.coa!); // ((txnPrev.coa.id !== txn.coa.id) || (txnPrev.coa.type !== txn.coa.type));
    }
    case 'check': {
      return txnPrev.check && txn.check && txnPrev.check.number !== txn.check.number;
    }
    case 'attachments': {
      const sum1 = txn[field].sort((a, b) => (a > b ? 1 : -1)).reduce((total, num) => `${total}-${num}`, '');
      const sum2 = txnPrev[field].sort((a, b) => (a > b ? 1 : -1)).reduce((total, num) => `${total}-${num}`, '');
      return sum1 !== sum2;
    }
    case 'tags': {
      return !areTagsEqual(txn[field] as ImList<Tag>, txnPrev[field] as ImList<Tag>);
    }
    case 'split': {
      if (transactionsUtils.isSplitTxn(txn) !== transactionsUtils.isSplitTxn(txnPrev)) {
        return true;
      }
      // @ts-expect-error - FIXME: This expression is not callable
      const sum1 = txnPrev?.split?.items && txnPrev.split.items.sort(matchSplitCoaIds).reduce(reducerSplitSum, '');
      // @ts-expect-error - FIXME: reduce (This expression is not callable)
      const sum2 = txn?.split?.items && txn.split.items.sort(matchSplitCoaIds).reduce(reducerSplitSum, '');
      return sum1 !== sum2;
    }
    default:
      if (txnPrev) {
        return txn[field] !== txnPrev[field];
      }
  }

  return false;
}

export function getTxnDifferences(txn: TransactionRecord, txnPrev: TransactionRecord) {
  const fields: string[] = [
    'accountId',
    'postedOn',
    'payee',
    'coa',
    'amount',
    'userFlag',
    'memo',
    'state',
    'isReviewed',
    'tags',
    'check',
    'attachments',
    'split',
    'isExcludedFromReports',
    'isExcludedFromF2S',
    'isDeleted',
  ];
  const changed: string[] = [];
  fields.forEach((field) => {
    if (fieldChanged(txn, txnPrev, field)) {
      changed.push(field);
    }
  });

  return changed;
}

// The code below is used to find possible transfers for new transactions being inserted.  When a new
// transfer transaction is entered, we look in the transfer account for a possible transaction match that
// may already be there, and offer the user to match to that transaction rather than create a dup
function isPossibleMatch(txn: TransactionRecord, postedOn: string, amount: string | number, days = 31) {
  const diff = DateTime.fromISO(postedOn).diff(DateTime.fromISO(txn.postedOn), 'days').days;
  return (
    !transactionsUtils.isSplitTxn(txn) &&
    !transactionsUtils.isTransferTxn(txn) &&
    Math.abs(diff) <= days &&
    Number(amount) === Number(txn.amount)
  );
}

export function sortTxnsByDateDifference(txns: ImList<TransactionRecord>, referenceDate: string) {
  const referenceDateTime = DateTime.fromISO(referenceDate);
  return txns.sort((a, b) => {
    const diff1 = Math.abs(DateTime.fromISO(a.postedOn).diff(referenceDateTime, 'days').days); // Math.abs(moment(a.postedOn).diff(moment(referenceDate), 'days'));
    const diff2 = Math.abs(DateTime.fromISO(b.postedOn).diff(referenceDateTime, 'days').days);
    if (diff1 === diff2) {
      return 0;
    }
    if (diff1 > diff2) {
      return 1;
    }
    return -1;
  });
}

export function findPossibleTransferCandidate(
  accountId: string,
  postedOn: string,
  amount: string | number,
  txnsByAccountId: ImMap<string, ImList<TransactionRecord>>,
  days: number,
) {
  const acctTxns = txnsByAccountId.get(accountId) || ImList([]);
  const txns = acctTxns.filter((txn: TransactionRecord) => isPossibleMatch(txn, postedOn, amount, days)).toList();
  if (txns) {
    return sortTxnsByDateDifference(txns, postedOn).first();
  }

  return null;
}

export function possiblyMatchTransfer(_txn: TransactionRecord, txnsByAccountId: ImMap<string, ImList<TransactionRecord>>) {
  let txn = _txn;
  if (
    !transactionsUtils.isSplitTxn(txn) &&
    !transactionsUtils.isTransferTxn(txn) &&
    txn.coa &&
    chartOfAccountsUtils.isL1TransferCoa(txn.coa)
  ) {
    let matches: ImList<TransactionRecord> = ImList();
    txnsByAccountId.forEach((acctTxns, acctId) => {
      if (acctId !== txn.accountId) {
        // check if there is a matching transaction and score it
        const match = findPossibleTransferCandidate(acctId, txn.postedOn, -txn.amount, txnsByAccountId, 5);
        if (match) {
          matches = matches.push(match);
        }
      }
    });
    if (matches.size > 0) {
      const bestMatch = sortTxnsByDateDifference(matches, txn.postedOn).first();
      txn = txn.set('coa', { type: 'ACCOUNT', id: bestMatch?.accountId ?? '' });
    }
  }
  return txn;
}

export function transactionsSortFunction(txn1: TransactionRecord, txn2: TransactionRecord) {
  // note, these are day boundary values constructed by convertToUnixDay
  // @ts-expect-error - FIXME: type issue ts(2363)
  let cmpValue = DateTime.fromISO(txn1.postedOn) - DateTime.fromISO(txn2.postedOn);

  if (cmpValue === 0) {
    // we sort balance adjustments to the top
    if ((txn1.coa && txn1.coa.type === 'BALANCE_ADJUSTMENT') || txn2.id?.substr(0, 3) === 'new') {
      cmpValue = -1;
    } else if ((txn2.coa && txn2.coa.type === 'BALANCE_ADJUSTMENT') || txn1.id?.substr(0, 3) === 'new') {
      cmpValue = 1;
    } else {
      const va = Number(txn1.id) || Number(txn2.id) + 1;
      const vb = Number(txn2.id) || Number(txn1.id) + 1;
      cmpValue = va - vb;
    }
  }
  return cmpValue;
}

// TODO Need UNIT TESTS for this potentially data damaging functionality

// determine if this transaction has the given tag id in it
export function txnHasTag(txn: TransactionRecord, tag: Tag) {
  if (transactionsUtils.isSplitTxn(txn)) {
    const ret = txn?.split?.items && txn.split.items.find((item) => item.tags && item.tags.find((rec) => rec.id === tag.id));
    return Boolean(ret);
  }
  // @ts-expect-error - FIXME: This expression is not callable
  return txn.tags && txn.tags.filter((tagItem: Tag) => tagItem.id === tag.id).size > 0;
}

// remove the reference to the given tag id from the transaction
export function removeTxnTag(txn: TransactionRecord, tag: Tag) {
  if (transactionsUtils.isSplitTxn(txn)) {
    const removeTagFromItem = (item: Record<string, any>) => {
      if (!item?.tags) {
        return item;
      }
      const updatedTags = item.tags.filter((tagItem: Tag) => tagItem.id !== tag.id);
      return item.set ? item.set('tags', updatedTags) : { ...item, tags: updatedTags };
    };
    return txn.setIn(
      ['split', 'items'],
      txn?.split?.items && txn.split.items.map((item) => removeTagFromItem(item)),
    );
  }
  // @ts-expect-error - FIXME: This expression is not callable
  return txn.set('tags', txn.tags && txn.tags.filter((tagItem: Tag) => tagItem.id !== tag.id));
}

// simple protected dateTime check
export function dateTimeFromTxn(txn: TransactionRecord) {
  return (
    (txn.postedOn && DateTime.fromISO(txn.postedOn)) ||
    (txn.stDueOn && DateTime.fromISO(txn.stDueOn)) ||
    DateTime.local()
  ).startOf('day');
}

/*
 * deDupeTxns
 *
 * Given a immutable list of transactions, will identify duplicates by txnId, and remove them so the returned list
 * contains only unique transactions
 */
export function deDupeTxns(txns: ImList<TransactionRecord>) {
  let exclusionSet = ImmutableSet();
  return txns.filter((txn) => {
    const dup = exclusionSet.includes(txn.id);
    exclusionSet = exclusionSet.add(txn.id);
    return !dup;
  });
}

/*
 * CalendarTxnType can be [income | expense | overdue | normal | more]
 */
export const getCalendarTxnType = (txn: TransactionRecord, incomeCOAs: ChartOfAccount) => {
  const txnState = getTxnStateInfo(txn);
  const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);
  let type = transactionsTypes.calTxnLabelTypes.normal;
  if (txnState.status === 'OVERDUE') {
    type = transactionsTypes.calTxnLabelTypes.overdue;
  }
  if (txnState.status !== 'OVERDUE' && txnState.entry === 'scheduled') {
    type = isIncome ? transactionsTypes.calTxnLabelTypes.income : transactionsTypes.calTxnLabelTypes.expense;
    if (isAcceptedScheduledTxn(txn)) {
      type = transactionsTypes.calTxnLabelTypes.normal; // Entered txn
    }
  }

  return type;
};

export const isRefundVirtualTxn = (txn: TransactionRecord) =>
  txn?.subtype === 'REFUND_SHADOW_TXN' || (txn?.subtype === 'REFUND' && txn?.state === 'PENDING');

export const isRefundTxn = (txn: TransactionRecord) => txn?.subtype === 'REFUND_SHADOW_TXN' || txn?.subtype === 'REFUND';

export const RefundState = Object.freeze({
  EXPECTED: 'EXPECTED',
  OVERDUE: 'OVERDUE',
  COMPLETE: 'COMPLETE',
  SHADOW: 'SHADOW',
  NONREFUND: 'NONREFUND',
});

export const getRefundState = (transaction: TransactionRecord) => {
  let refundState: string;
  switch (transaction.subtype) {
    case 'REFUND':
      if (transaction.state === 'PENDING') {
        const dateNow = DateTime.local().startOf('day');
        const dateRefund = DateTime.fromISO(transaction.postedOn);
        if (dateRefund < dateNow) {
          refundState = RefundState.OVERDUE;
        } else {
          refundState = RefundState.EXPECTED;
        }
      } else {
        refundState = RefundState.COMPLETE;
      }
      break;
    case 'REFUND_SHADOW_TXN':
      refundState = RefundState.SHADOW;
      break;
    default:
      refundState = RefundState.NONREFUND;
  }

  return refundState;
};

// -------------------------------------------------------
// updateNewTags
//
// takes an immutable transaction, and checks for tags with ID="0" and tries to
// find a tag that is suitable with a real ID.  Returns an immutable transaction
// with updates if they were needed
export function updateNewTags(txn: TransactionRecord, dropPlaceholders = false, state = store.getState()) {
  let retTxn = txn;
  if (txn.tags) {
    const visited: Tag[] = [];
    const newTags = txn.tags
    // @ts-expect-error - FIXME: This expression is not callable
      .filter((tag: Tag) => {
        // remove dupes or empty entries
        if (tag) {
          const alreadyHas = visited.find((x) => x.id === tag.id && (x.id !== '0' || x.name === tag.name));
          if (!alreadyHas) {
            visited.push(tag);
            return true;
          }
        }
        return false;
      })
      .map((tag: Tag) => {
        // find current tag
        if (noNaN(tag?.id) === 0) {
          const ret = tagsSelectors.getTagByName(state, tag.name!);
          return ret && ret.id ? { id: ret.id } : tag;
        }
        return tag;
      });
    retTxn = txn.set(
      'tags',
      newTags.filter((tagItem: Tag) => tagItem !== null && (!dropPlaceholders || noNaN(tagItem?.id) !== 0)),
    );
  }
  if (transactionsUtils.isSplitTxn(retTxn)) {
    const newItems =
      retTxn?.split?.items &&
      retTxn.split.items.map((item: SplitItem) => {
        if (item?.tags) {
          const newSplitTags = item.tags.map((tag: Tag) => {
            if (noNaN(tag.id) === 0) {
              const ret = tagsSelectors.getTagByName(store.getState(), tag.name!);
              if (ret && ret.id) {
                return { id: ret.id };
              }
              return dropPlaceholders ? null : tag;
            }
            return tag;
          });
          if (item.set && typeof item.set === 'function') {
            // immutable object
            return (
              item?.set(
                'tags',
                // @ts-expect-error - FIXME: This expression is not callable
                newSplitTags.filter((splitTag: Tag) => splitTag !== null && (!dropPlaceholders || noNaN(splitTag.id) !== 0)),
              ) || item
            );
          }
          const newItem = item;
          // @ts-expect-error - FIXME: Cannot assign to tags because it is a read-only property ts(2540)
          newItem.tags = newSplitTags.filter((splitTag: Tag) => splitTag !== null && (!dropPlaceholders || noNaN(splitTag.id) !== 0)) || item;
          return newItem;
        }
        return item;
      });

    retTxn = retTxn.setIn(['split', 'items'], newItems);
  }
  return retTxn;
}

/**
 * memorizedPayee list of memorized payees & payees list from txns
 * @param {*} txn
 * @param {*} memorizedPayee
 * @returns txn with memorized payee values
 */
export function updateMemorizePayeeValues(txn, memorizedPayee) {
  let retTxn = txn;
  // downloaded txn amount is not changed
  if (!retTxn.get('cpData') && !memorizedPayee.isAmountBasedRule && memorizedPayee.isMemorizedPayee) {
    retTxn = retTxn.set('amount', memorizedPayee.txn.amount);
  }
  if (transactionsUtils.isSplitTxn(memorizedPayee.txn)) {
    // We need to null out the id's for the split items
    /* eslint-disable arrow-body-style */
    let newSplitItems = memorizedPayee.txn.split.items.map((item) => {
      return { ...item.toJS(), id: null };
    });
    // add adjustment amount with split for downloaded txn
    if (retTxn.get('cpData')) {
      const adjAmount = txn.get('amount') - sumSplitRows(newSplitItems);
      const newRow = {
        memo: '',
        tags: [],
        coa: { type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED, id: '0' },
        amount: numeral(adjAmount).format('0.00'),
      };
      newSplitItems = newSplitItems.push(newRow);
    } else {
      retTxn = retTxn.set('amount', memorizedPayee.txn.amount);
    }
    retTxn = retTxn.set('split', transactionsTransformers.mkSplit({ items: newSplitItems }));
    retTxn = retTxn.set('coa', null);
  } else {
    retTxn = retTxn.set('coa', memorizedPayee.txn.coa);
    retTxn = retTxn.set('split', null);
  }
  retTxn = retTxn.set('tags', memorizedPayee.isMemorizedPayee ? memorizedPayee.txn.tags : null);
  retTxn = retTxn.set('memo', memorizedPayee.isMemorizedPayee ? memorizedPayee.txn.memo : null);

  if (memorizedPayee.isMemorizedPayee) {
    retTxn = retTxn.set('state', memorizedPayee.markAsCleared ? TransactionStatus.CLEARED : TransactionStatus.PENDING);
  }

  return retTxn;
}

/**
 * txnCategorySign
 * @param {*} txn
 * @returns
 */
export function txnCategorySign(txn: TransactionRecord) {
  if (transactionsUtils.isSplitTxn(txn)) {
    return 1;
  }
  const isIncomeCat = txn.coa ? categoriesSelectors.isIncomeCat(null, txn.coa.id) : false;

  return isIncomeCat ? 1 : -1;
}

export const shouldShowReviewField = (accountId: string) => {
  const account = accountsSelectors.getAccountById(store?.getState(), accountId);
  const isQWinDataset = authSelectors.getIsWinDataset(store.getState());

  return isQWinDataset ? account && !isAccountDisconnected(account) : true;
};

export const shouldDisableReviewField = (transaction: TransactionRecord) => {
  const isQWinDataset = authSelectors.getIsWinDataset(store.getState());
  return isQWinDataset ? transaction.isReviewed : false;
};

export const isC2RTransaction = (txn: TransactionRecord, txnAccount?: Account) => {
  const account = txnAccount ?? accountsSelectors.getAccountById(store?.getState(), txn.accountId);
  return !txn.isReviewed
   && account?.isC2REnabled
   && !isAccountDisconnected(account!)
   && getMatchState(txn) === 'downloaded'
   && txn.state !== 'PENDING';
};
